import flatMap from 'lodash/flatMap';
import map from 'lodash/map';
import cloneDeep from 'lodash/cloneDeep';
import difference from 'lodash/difference';
import { operators as o } from '@pendo/aggregations';
import { getIntegration, saveJoinKeys } from '@/integrations/sf/sync.utils';

export const SALESFORCE_PUSH_FLAG = 'salesforce-push';

export function SFReportModifier (segmentId, columns, kind) {
    return function (agg) {
        agg.pipeline = modifySFPipeline(agg.pipeline, kind);

        return agg;
    };
}

// Every source `getSource` in visitor-list.aggregation.js can start the events branch from.
const EVENT_SOURCES = ['events', 'pageEvents', 'featureEvents', 'trackEvents', 'agenticConversationEvents'];

function hasEventSource (branch) {
    const source = branch[0]?.source;

    return Boolean(source) && EVENT_SOURCES.some((sourceName) => sourceName in source);
}

function modifySFPipeline (pipeline, kind, keepSource = false) {
    const aggColumnRegex = /daysActive|eventCount|eventTime|numVisitors/i;

    const modified = flatMap(pipeline, (operator) => {
        // Swap only event-sourced branches; the others already emit the rows (e.g. email-only) they join in.
        if (operator.spawn) {
            return {
                spawn: operator.spawn.map((branch) => modifySFPipeline(branch, kind, !hasEventSource(branch))),
            };
        }

        if (operator.source && !keepSource) {
            // Always use visitor source to support segments that might include visitorId
            const sourceAggList = [
                o.sources.visitors(),
                // always unwind accounts for segments that might contain account rules
                o.evaluate('accountId', 'metadata.auto.accountids'),
                kind === 'account' && o.filter('accountId != null'),
                o.unwind('accountId', { keepEmpty: true }),
            ].filter(Boolean);

            // base agg source around including all accounts or visitors
            return sourceAggList;
        }

        if (operator.identified) {
            return o.identified('visitorId');
        }

        if (operator.select) {
            // modify selects to request 0s instead of nulls for calculated values.
            // Copy first: the builder can share one select object between spawn branches.
            const select = { ...operator.select };
            Object.keys(select).forEach((key) => {
                if (aggColumnRegex.test(key)) {
                    select[key] = `if(isNull(${key}), 0, ${key})`;
                }
            });

            return {
                select,
            };
        }

        return operator;
    });

    // A kept source has no accountId filter of its own, and rows only get accountId once the branch selects it.
    return keepSource && kind === 'account' ? [...modified, o.filter('accountId != null')] : modified;
}

export async function updateReportMapping (report) {
    if (hasFlag(report, SALESFORCE_PUSH_FLAG)) {
        const configuration = await getIntegration();

        if (configuration?.data?.reports && configuration.data.reports[report.id]) {
            const reportConfig = configuration.data.reports[report.id];
            const mappedColumnIds = Object.keys(reportConfig.fields);
            const reportColumnIds = map(report.definition.config.columns, 'columnName');
            const needToUnmap = difference(mappedColumnIds, reportColumnIds);

            if (needToUnmap.length) {
                // Remove field(s) from SF push configuration
                needToUnmap.forEach((columnId) => {
                    delete reportConfig.fields[columnId];
                });
                await saveJoinKeys(configuration, configuration.data);
            }
        }

        return report;
    }

    return report;
}

export async function removeReportMapping (report) {
    if (hasFlag(report, SALESFORCE_PUSH_FLAG)) {
        const configuration = await getIntegration();

        if (configuration?.data?.reports && configuration.data.reports[report.id]) {
            // Remove report from SF push configuration
            delete configuration.data.reports[report.id];
            await saveJoinKeys(configuration, configuration.data);
        }

        return report;
    }

    return report;
}

export function addFlag (report, flag) {
    report.flags = report.flags || [];

    if (!hasFlag(report, flag)) {
        report.flags.push(flag);
    }

    return report;
}

export function removeFlag (report, flag) {
    report.flags = report.flags || [];

    const index = report.flags.indexOf(flag);
    if (index >= 0) {
        report.flags.splice(index, 1);
    }

    return report;
}

export function hasFlag (report, flag) {
    if (!report || !report.flags) return false;

    return report.flags.includes(flag);
}

export function updateReportForSalesforcePush (report) {
    const updatedReport = cloneDeep(report);

    // We unconditionally apply these every time for some reason
    updatedReport.definition.config.everyone = true; // include all visitors/accounts
    addFlag(updatedReport, SALESFORCE_PUSH_FLAG);

    return updatedReport;
}
